import { DatasetRef } from 'components/shared/Compare';
import {
  ComparisonContribution,
  ComparisonDatasetHeader,
  ComparisonIndicator,
  ComparisonResponse,
} from 'hooks/data/datasets/comparison/types';

export type Contribution = { key: string; label: string; share: number; impact: number };

export type ComparedIndicator = {
  key: string;
  category: string;
  indicator: string;
  unit: string;
  amounts: [number | undefined, number | undefined];
  contributions: [Contribution[], Contribution[]];
};

export type ComparedIndicators = {
  indicators: ComparedIndicator[];
  hasIdMismatch: boolean;
};

const toContributions = (contributions: ComparisonContribution[] = []): Contribution[] =>
  contributions.map((contribution, position) => ({
    // Position, not flow index or name+compartment: neither is unique within an indicator.
    key: String(position),
    label: contribution.meta.name || contribution.meta.subcomp,
    share: contribution.relative_contribution,
    impact: contribution.impact,
  }));

// Match the full release, not the id: comparing a dataset with its own earlier release requests the same id twice.
const resolvePositions = (headers: ComparisonDatasetHeader[], pair: [DatasetRef, DatasetRef]): [number, number] | null => {
  if (headers.length !== 2) return null;

  const [positionA, positionB] = pair.map((ref) =>
    headers.findIndex(
      (header) => header.index === Number(ref.id) && header.version === ref.version && header.system_model === ref.systemModel
    )
  );
  if (positionA === -1 || positionB === -1 || positionA === positionB) return null;

  return [positionA, positionB];
};

// apollo-api contract: results follow header order, so a shared id is told apart by position only.
const resultsMatchHeaders = (indicator: ComparisonIndicator, headers: ComparisonDatasetHeader[]): boolean =>
  indicator.results.length === headers.length &&
  indicator.results.every((result, position) => result.dataset_id === headers[position].index);

const toComparedIndicator = (indicator: ComparisonIndicator, [positionA, positionB]: [number, number]): ComparedIndicator => {
  const [resultA, resultB] = [indicator.results[positionA], indicator.results[positionB]];

  return {
    key: `${indicator.category_name}|${indicator.indicator_name}`,
    category: indicator.category_name,
    indicator: indicator.indicator_name,
    unit: indicator.unit_name,
    amounts: [resultA.amount, resultB.amount],
    contributions: [toContributions(resultA.direct_contributions), toContributions(resultB.direct_contributions)],
  };
};

export const buildComparedIndicators = (
  payload: ComparisonResponse | undefined,
  requested: [DatasetRef | undefined, DatasetRef | undefined]
): ComparedIndicators => {
  const [refA, refB] = requested;
  if (!payload || !refA || !refB) return { indicators: [], hasIdMismatch: false };

  const positions = resolvePositions(payload.datasets, [refA, refB]);
  if (!positions || !payload.indicators.every((indicator) => resultsMatchHeaders(indicator, payload.datasets))) {
    return { indicators: [], hasIdMismatch: true };
  }

  return { indicators: payload.indicators.map((indicator) => toComparedIndicator(indicator, positions)), hasIdMismatch: false };
};
